import { useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { 
  ZombieSessionConfig, 
  AuthError, 
  AUTH_ERROR_PATTERNS,
  AUTH_ERROR_MESSAGES,
  AuthErrorDetails 
} from '@/types/auth.types';

/**
 * Hook to detect and handle zombie sessions, banned accounts, and auth errors
 * Implements aggressive cleanup and intelligent retry logic
 */
export const useZombieSessionHandler = (config: ZombieSessionConfig = {}) => {
  const {
    maxRetries = 3,
    retryDelay = 1000,
    aggressiveCleanup = true,
    onZombieDetected,
    onSessionCleared,
    onBanDetected
  } = config;

  /**
   * Parse auth errors to detect bans and other issues
   */
  const parseAuthError = useCallback((error: any): AuthError => {
    const errorMessage = error?.message || error?.error_description || '';
    const errorCode = error?.code || error?.error || 'unknown';
    const status = error?.status;

    console.log('🔍 [ZombieHandler] Parsing error:', { errorMessage, errorCode, status });

    // Check for ban patterns
    if (AUTH_ERROR_PATTERNS.BANNED.test(errorMessage)) {
      const details: AuthErrorDetails = {
        reason: 'banned',
        contact: 'support@stonkfu.life'
      };
      
      // Try to extract suspension duration if mentioned
      const untilMatch = errorMessage.match(/until\s+(\d{4}-\d{2}-\d{2})/);
      if (untilMatch) {
        details.until = untilMatch[1];
        details.reason = 'suspended';
      }

      onBanDetected?.(details);
      
      return {
        code: 'auth/account-banned',
        message: AUTH_ERROR_MESSAGES[details.reason],
        status: 403,
        details
      };
    }

    // Check other patterns
    if (AUTH_ERROR_PATTERNS.EMAIL_NOT_VERIFIED.test(errorMessage)) {
      return {
        code: 'auth/email-not-verified',
        message: AUTH_ERROR_MESSAGES.email_not_verified,
        status: 403,
        details: { requiresAction: 'verify_email' }
      };
    }

    if (AUTH_ERROR_PATTERNS.RATE_LIMITED.test(errorMessage)) {
      return {
        code: 'auth/rate-limited',
        message: AUTH_ERROR_MESSAGES.rate_limited,
        status: 429
      };
    }

    if (AUTH_ERROR_PATTERNS.SESSION_EXPIRED.test(errorMessage)) {
      return {
        code: 'auth/session-expired',
        message: AUTH_ERROR_MESSAGES.session_expired,
        status: 401
      };
    }

    if (AUTH_ERROR_PATTERNS.NETWORK_ERROR.test(errorMessage)) {
      return {
        code: 'auth/network-error',
        message: AUTH_ERROR_MESSAGES.network_error,
        status: 0
      };
    }

    // Default error
    return {
      code: errorCode,
      message: errorMessage || AUTH_ERROR_MESSAGES.unknown,
      status
    };
  }, [onBanDetected]);

  /**
   * Super aggressive deployment cache clearing for persistent OAuth issues
   */
  const clearDeploymentCache = useCallback(async () => {
    console.log('🚀 [ZombieHandler] SUPER AGGRESSIVE deployment cache clearing...');
    
    try {
      // First, force sign out with global scope
      console.log('🚀 [ZombieHandler] Global Supabase signout...');
      await supabase.auth.signOut({ scope: 'global' });
      
      // Clear ALL localStorage (nuclear option for deployment issues)
      console.log('🚀 [ZombieHandler] Nuclear localStorage clear...');
      const allLocalStorageKeys = Object.keys(localStorage);
      allLocalStorageKeys.forEach(key => {
        console.log(`🗑️ [ZombieHandler] Nuking localStorage: ${key}`);
        localStorage.removeItem(key);
      });
      
      // Clear ALL sessionStorage (nuclear option)
      console.log('🚀 [ZombieHandler] Nuclear sessionStorage clear...');
      const allSessionStorageKeys = Object.keys(sessionStorage);
      allSessionStorageKeys.forEach(key => {
        console.log(`🗑️ [ZombieHandler] Nuking sessionStorage: ${key}`);
        sessionStorage.removeItem(key);
      });
      
      // Nuclear cookie clearing
      console.log('🚀 [ZombieHandler] Nuclear cookie clear...');
      document.cookie.split(";").forEach((c) => {
        const cookie = c.trim();
        const eqPos = cookie.indexOf("=");
        const name = eqPos > -1 ? cookie.substr(0, eqPos) : cookie;
        
        // Clear ALL cookies for deployment cache issues
        const expiration = 'expires=Thu, 01 Jan 1970 00:00:00 GMT';
        const domains = [
          '',
          `;domain=${window.location.hostname}`,
          `;domain=.${window.location.hostname}`,
          `;domain=.netlify.app`,
          `;domain=.stonkfu.life`,
          `;domain=.supabase.co`,
          `;domain=.google.com`,
          `;domain=.discord.com`
        ];
        
        domains.forEach(domain => {
          document.cookie = `${name}=;${expiration};path=/${domain}`;
          console.log(`🗑️ [ZombieHandler] Nuked cookie: ${name} for domain: ${domain || 'current'}`);
        });
      });
      
      // Clear any browser caches we can access
      if ('caches' in window) {
        console.log('🚀 [ZombieHandler] Clearing service worker caches...');
        const cacheNames = await caches.keys();
        await Promise.all(
          cacheNames.map(cacheName => {
            console.log(`🗑️ [ZombieHandler] Deleting cache: ${cacheName}`);
            return caches.delete(cacheName);
          })
        );
      }
      
      // Clear IndexedDB if possible
      if ('indexedDB' in window) {
        console.log('🚀 [ZombieHandler] Clearing IndexedDB...');
        try {
          // Get all databases and clear them
          const databases = await indexedDB.databases();
          await Promise.all(
            databases.map(db => {
              if (db.name) {
                console.log(`🗑️ [ZombieHandler] Deleting IndexedDB: ${db.name}`);
                const deleteRequest = indexedDB.deleteDatabase(db.name);
                return new Promise((resolve, reject) => {
                  deleteRequest.onsuccess = () => resolve(undefined);
                  deleteRequest.onerror = () => reject(deleteRequest.error);
                });
              }
            })
          );
        } catch (error) {
          console.warn('⚠️ [ZombieHandler] IndexedDB clear failed:', error);
        }
      }
      
      // Wait longer for cleanup to complete
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      console.log('✅ [ZombieHandler] NUCLEAR deployment cache clearing complete');
    } catch (error) {
      console.error('❌ [ZombieHandler] Error during nuclear cache clear:', error);
    }
  }, []);

  /**
   * Aggressively clear all auth-related data
   */
  const clearAllAuthData = useCallback(async () => {
    console.log('🧹 [ZombieHandler] Starting aggressive auth data cleanup...');
    
    try {
      // First clear deployment cache
      await clearDeploymentCache();
      
      // Sign out from Supabase
      await supabase.auth.signOut();
      
      if (aggressiveCleanup) {
        // Clear all localStorage items related to auth (enhanced for deployment cache issues)
        const keysToRemove: string[] = [];
        for (let i = 0; i < localStorage.length; i++) {
          const key = localStorage.key(i);
          if (key && (
            key.includes('supabase') || 
            key.includes('auth') || 
            key.includes('session') ||
            key.includes('token') ||
            key.includes('oauth') ||
            key.includes('google') ||
            key.includes('discord') ||
            key.includes('github') ||
            key.includes('apple') ||
            key.includes('sb-') ||  // Supabase prefixed keys
            key.includes('state') ||  // OAuth state parameters
            key.includes('code_verifier') ||  // PKCE codes
            key.includes('redirect') ||  // OAuth redirects
            key.includes('pkce') ||  // PKCE-specific
            key.includes('nonce') ||  // OAuth nonce
            key.includes('code_challenge') ||  // PKCE challenge
            key.includes('auth-token-code-verifier') // Supabase specific PKCE
          )) {
            keysToRemove.push(key);
          }
        }
        
        keysToRemove.forEach(key => {
          console.log(`🗑️ [ZombieHandler] Removing localStorage: ${key}`);
          localStorage.removeItem(key);
        });
        
        // Clear session storage (enhanced for deployment cache issues)
        const sessionKeysToRemove: string[] = [];
        for (let i = 0; i < sessionStorage.length; i++) {
          const key = sessionStorage.key(i);
          if (key && (
            key.includes('supabase') || 
            key.includes('auth') || 
            key.includes('session') ||
            key.includes('token') ||
            key.includes('oauth') ||
            key.includes('pkce') ||
            key.includes('nonce') ||
            key.includes('code_verifier') ||
            key.includes('code_challenge') ||
            key.includes('auth-token-code-verifier') ||
            key.includes('oauth') ||
            key.includes('google') ||
            key.includes('discord') ||
            key.includes('github') ||
            key.includes('apple') ||
            key.includes('sb-') ||  // Supabase prefixed keys
            key.includes('state') ||  // OAuth state parameters
            key.includes('code_verifier') ||  // PKCE codes
            key.includes('redirect')  // OAuth redirects
          )) {
            sessionKeysToRemove.push(key);
          }
        }
        
        sessionKeysToRemove.forEach(key => {
          console.log(`🗑️ [ZombieHandler] Removing sessionStorage: ${key}`);
          sessionStorage.removeItem(key);
        });
        
        // Clear auth-related cookies (enhanced for deployment cache issues)
        document.cookie.split(";").forEach((c) => {
          const cookie = c.trim();
          const eqPos = cookie.indexOf("=");
          const name = eqPos > -1 ? cookie.substr(0, eqPos) : cookie;
          if (name.includes('supabase') || 
              name.includes('auth') || 
              name.includes('sb-') ||
              name.includes('oauth') ||
              name.includes('google') ||
              name.includes('discord') ||
              name.includes('github') ||
              name.includes('apple') ||
              name.includes('session') ||
              name.includes('state') ||
              name.includes('code')) {
            // Clear for all possible domains and paths
            document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`;
            document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;domain=${window.location.hostname}`;
            document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;domain=.${window.location.hostname}`;
            document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;domain=.netlify.app`;
            document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;domain=.stonkfu.life`;
            console.log(`🗑️ [ZombieHandler] Cleared cookie: ${name}`);
          }
        });
      }
      
      console.log('✅ [ZombieHandler] Auth data cleanup complete');
      onSessionCleared?.();
    } catch (error) {
      console.error('❌ [ZombieHandler] Error during cleanup:', error);
    }
  }, [clearDeploymentCache, aggressiveCleanup, onSessionCleared]);

  /**
   * Detect zombie sessions by verifying session validity
   */
  const detectAndClearZombieSession = useCallback(async (): Promise<boolean> => {
    try {
      console.log('🧟 [ZombieHandler] Checking for zombie sessions...');
      
      // Get current session
      const { data: { session }, error } = await supabase.auth.getSession();
      
      if (error) {
        console.error('🧟 [ZombieHandler] Session check error:', error);
        const parsedError = parseAuthError(error);
        
        // If it's a ban error, don't clear session immediately
        if (parsedError.details?.reason === 'banned' || parsedError.details?.reason === 'suspended') {
          toast.error(parsedError.message);
          return false;
        }
        
        return true; // Assume zombie if error
      }

      // If we have a session, verify it's actually valid
      if (session) {
        try {
          // Try to get user data to verify session validity
          const { data: { user }, error: userError } = await supabase.auth.getUser();
          
          if (userError || !user) {
            console.warn('🧟 [ZombieHandler] Zombie session detected!');
            onZombieDetected?.();
            
            // Clear the zombie session
            await clearAllAuthData();
            return true;
          }
          
          // Check if user is banned in metadata
          if (user.app_metadata?.banned || user.app_metadata?.account_status === 'banned') {
            console.warn('🚫 [ZombieHandler] Banned user detected');
            onBanDetected?.({ reason: 'banned', contact: 'support@stonkfu.life' });
            toast.error(AUTH_ERROR_MESSAGES.banned);
            await clearAllAuthData();
            return false;
          }
          
          // Check suspension
          if (user.app_metadata?.suspended_until) {
            const suspendedUntil = new Date(user.app_metadata.suspended_until);
            if (suspendedUntil > new Date()) {
              console.warn('⏸️ [ZombieHandler] Suspended user detected');
              onBanDetected?.({ 
                reason: 'suspended', 
                until: suspendedUntil.toISOString(),
                contact: 'support@stonkfu.life' 
              });
              toast.error(AUTH_ERROR_MESSAGES.suspended);
              await clearAllAuthData();
              return false;
            }
          }
        } catch (e) {
          console.error('🧟 [ZombieHandler] Failed to verify session:', e);
          await clearAllAuthData();
          return true;
        }
      }

      console.log('✅ [ZombieHandler] No zombie sessions found');
      return false;
    } catch (error) {
      console.error('🧟 [ZombieHandler] Unexpected error:', error);
      return false;
    }
  }, [clearAllAuthData, onZombieDetected, onBanDetected, parseAuthError]);

  /**
   * Force OAuth retry with exponential backoff
   */
  const forceOAuthRetry = useCallback(async (
    provider: string, 
    retryCount = 0
  ): Promise<{ success: boolean; error?: AuthError }> => {
    if (retryCount >= maxRetries) {
      console.error(`🧟 [ZombieHandler] Max retries (${maxRetries}) reached for ${provider}`);
      return { 
        success: false, 
        error: {
          code: 'auth/max-retries',
          message: 'Maximum retry attempts reached. Please try again later.'
        }
      };
    }

    console.log(`🔄 [ZombieHandler] OAuth retry ${retryCount + 1}/${maxRetries} for ${provider}`);
    
    // Clear any existing sessions
    await clearAllAuthData();
    
    // Exponential backoff
    const delay = retryDelay * Math.pow(2, retryCount);
    console.log(`⏱️ [ZombieHandler] Waiting ${delay}ms before retry...`);
    await new Promise(resolve => setTimeout(resolve, delay));
    
    // Check if zombie session was cleared
    const hasZombie = await detectAndClearZombieSession();
    
    if (hasZombie) {
      // If we found and cleared a zombie, try again
      return forceOAuthRetry(provider, retryCount + 1);
    }
    
    return { success: true };
  }, [maxRetries, retryDelay, clearAllAuthData, detectAndClearZombieSession]);

  /**
   * Check session health periodically
   */
  const checkSessionHealth = useCallback(async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      
      if (session) {
        const expiresAt = new Date(session.expires_at! * 1000);
        const now = new Date();
        const timeUntilExpiry = expiresAt.getTime() - now.getTime();
        
        // Warn if session expires in less than 5 minutes
        if (timeUntilExpiry < 5 * 60 * 1000 && timeUntilExpiry > 0) {
          console.warn('⚠️ [ZombieHandler] Session expiring soon:', {
            expiresAt: expiresAt.toISOString(),
            minutesLeft: Math.floor(timeUntilExpiry / 60000)
          });
          
          // Try to refresh the session
          const { error } = await supabase.auth.refreshSession();
          if (error) {
            console.error('❌ [ZombieHandler] Failed to refresh session:', error);
          } else {
            console.log('✅ [ZombieHandler] Session refreshed successfully');
          }
        }
      }
    } catch (error) {
      console.error('❌ [ZombieHandler] Session health check error:', error);
    }
  }, []);

  // Run zombie detection on mount
  useEffect(() => {
    detectAndClearZombieSession();
    
    // Set up periodic session health checks (every 2 minutes)
    const healthCheckInterval = setInterval(checkSessionHealth, 2 * 60 * 1000);
    
    return () => clearInterval(healthCheckInterval);
  }, [detectAndClearZombieSession, checkSessionHealth]);

  return {
    detectAndClearZombieSession,
    clearAllAuthData,
    clearDeploymentCache,
    forceOAuthRetry,
    parseAuthError,
    checkSessionHealth
  };
};